import React, { useCallback, useMemo, useEffect } from 'react';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';
import stringToSlug from 'helpers/stringToSlug';
import formatDate from 'helpers/formatDate';
import useDateRangeKPI from 'hooks/useDateRangeKPI';
import useMedia from 'hooks/useMedia';

import mapKPIData from '../mappings/mapKPIData';
import useExportUrl from './utils/useExportUrl';

import KeyValueGroup from 'components/base/KeyValueGroup/KeyValueGroup';
import { ReactComponent as DiscoveryIcon } from 'icons/topics/discovery.svg';
import {
  Container,
  Header,
  KPIItem as KPIListItem,
  KPIListWrapper,
  KPIListItemColumn,
  KPIListItemContent,
  KPIListItemContentItem,
  KPIListItemContentItemColumn,
  KPIListItemRow,
  KPIListTotal as KPIList,
  TWTDText,
  ListItemWrapper,
  DateRange,
} from '../shared';
import KPIWrapper from '../KPIWrapper/KPIWrapper';
import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import TopTerritories from './TopTerritories';
import { TopCities } from './TopCities';
import { TopCountry } from './TopCountry';
import {
  IDiscoveryProps,
  ITopTerritoriesEntityCityItem,
  ITopTerritoriesEntityCountryItem,
  ITopTerritoriesEntity,
} from './types';
import interceptConnect from 'helpers/interceptConnect';
import useCountryDiscoveryData from './utils/useDiscoveryCountriesData';
import useDiscoveryCityData from './utils/useDiscoveryCitiesData';
import { tooltipContent } from 'helpers/getTooltipContent';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import { stringify } from 'querystring';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

const dataSources = (): any => {
  return {
    discoveryFetch: (
      compareMode: string,
      countryIds: string[],
      entityIds: string[],
      entityType: string,
      filters: { country: string; region: string },
      customRegion: any
    ): any => {
      const params = stringify({
        compareMode,
        countryIds,
        entityIds,
        entityType,
        country: filters?.country,
        region: filters?.region,
        customRegion,
      });

      return {
        discoveryKPI: {
          url: `discoverykpi?${params}`,
          interceptionData: {
            compareMode,
            countryIds,
            entityIds,
            entityType,
            filters,
            customRegion,
          },
          comparison: `${compareMode} : ${
            countryIds && countryIds.join()
          }: ${entityIds.join()} : ${entityType} ${filters.country} ${
            filters.region
          } ${customRegion}`,
        },
      };
    },
  };
};
const DiscoveryKPIs: React.FC<IDiscoveryProps> = (props) => {
  const {
    compareMode,
    countryIds,
    dataColors,
    discoveryKPI = PromiseState.create(),
    entityIds,
    entityType,
    filters,
    discoveryFetch,
    customRegionFilter,
  } = props;

  const {
    discoveryKPIData,
    discoveryTWTDCountriesData,
    discoveryLWCountriesData,
    discoveryCitiesTWTD,
    discoveryCitiesLW,
    url,
    reason,
  } = (discoveryKPI && !discoveryKPI?.pending && discoveryKPI?.value) || {};

  useEffect(() => {
    discoveryFetch(
      compareMode,
      countryIds,
      entityIds,
      entityType,
      filters,
      customRegionFilter
    );
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters,
    discoveryFetch,
    customRegionFilter,
  ]);

  const topCountriesTWTD = useCountryDiscoveryData(discoveryTWTDCountriesData);
  const topCountriesLW = useCountryDiscoveryData(discoveryLWCountriesData);
  const topCitiesTWTD = useDiscoveryCityData(discoveryCitiesTWTD);
  const topCitiesLW = useDiscoveryCityData(discoveryCitiesLW);

  const isMobileView = useMedia('(max-width: 40em)');

  const [thisWeekToDate, lastWeek] = useDateRangeKPI('discoveries');

  const countryFiltering = filters.country || '';
  const exportUrl = useExportUrl(url);
  const compareArray = compareMode === 'country' ? countryIds : entityIds;
  const discoveryKPIs = useMemo(() => {
    if (discoveryKPIData && discoveryKPIData.length > 0) {
      return mapKPIData({
        data: discoveryKPIData,
        compareMode,
        compareArray,
      });
    }
  }, [discoveryKPIData, compareMode, compareArray]);

  /**
   * Returns a key for TopCountry list items
   */
  const getCountryKey = useCallback(
    (entity: ITopTerritoriesEntity, item: ITopTerritoriesEntityCountryItem) =>
      `top-country-${entity.entity.id}-${item.id}`,
    []
  );

  /**
   * Returns a key for TopCity list items
   */
  const getCitiesKey = useCallback(
    (entity: ITopTerritoriesEntity, item: ITopTerritoriesEntityCityItem) =>
      `top-city-${entity.entity.id}-${item.cityId}`,
    []
  );

  const KPIListItemBlock = compareMode ? KPIListItemColumn : KPIListItemRow;

  return (
    <Container data-pw={stringToSlug('Shazam KPIs')}>
      <ErrorBoundary
        error={reason?.error ? new Error(reason?.error) : null}
        errorInfo={reason?.errorInfo}
      >
        <KPIWrapper
          allowedFilters={['country', 'region', 'customRegion']}
          title="Shazam KPIs"
          glossarySlug="kpis/shazam-kpis"
          icon={DiscoveryIcon}
          extraToolbarItems={[
            {
              label: 'Export Shazam KPI data',
              href: exportUrl,
              type: IToolbarItemType.Export,
              order: 1,
            },
          ]}
          titleAlignLeft="-1.5em"
          alignSVG="1.9em"
        >
          <KPIListWrapper>
            <KPIList>
              <KPIListItem>
                <KPIListItemContent>
                  <KPIListItemContentItem itemsCount={3}>
                    <KPIListItemRow>
                      <ListItemWrapper>
                        <Header>
                          <TippyToolTip
                            enabled
                            tooltipContent={tooltipContent}
                            offset={[10, 25]}
                            maxWidth="170px"
                          >
                            <TWTDText>{`This Week ${
                              !isMobileView ? 'to Date' : ''
                            }`}</TWTDText>
                          </TippyToolTip>
                          {thisWeekToDate && (
                            <DateRange>
                              {`${formatDate(
                                thisWeekToDate?.startDate,
                                'day',
                                true
                              )} 
                              - ${formatDate(
                                thisWeekToDate?.endDate,
                                'day',
                                true
                              )}`}
                            </DateRange>
                          )}
                        </Header>
                        {discoveryKPI.pending && (
                          <KPILoader showPrevious showChange />
                        )}
                        {discoveryKPIs && (
                          <KeyValueGroup
                            items={[
                              {
                                label: 'Tags',
                                metric: delve(
                                  discoveryKPIs,
                                  'countThisWeekToDate'
                                ),
                              },
                              {
                                label: 'Previous',
                                metric: delve(
                                  discoveryKPIs,
                                  'countLastWeekToDate'
                                ),
                              },
                              {
                                label: 'Change',
                                metric: delve(discoveryKPIs, 'wowInProgress'),
                              },
                            ]}
                            compareMode={compareMode}
                            dataColors={dataColors}
                            isKPIValue
                          />
                        )}
                      </ListItemWrapper>
                    </KPIListItemRow>
                    <KPIListItemBlock>
                      <KPIListItemContentItemColumn>
                        <TopTerritories
                          label="Top Countries"
                          pending={discoveryKPI.pending}
                          topTerritories={topCountriesTWTD}
                          TerritoryItem={TopCountry}
                          fullName={!compareMode}
                          getKey={getCountryKey}
                        />
                      </KPIListItemContentItemColumn>
                      <KPIListItemContentItemColumn>
                        <TopTerritories
                          label={`Top ${countryFiltering} Cities`}
                          pending={discoveryKPI.pending}
                          topTerritories={topCitiesTWTD}
                          TerritoryItem={TopCities}
                          getKey={getCitiesKey}
                          type="city"
                        />
                      </KPIListItemContentItemColumn>
                    </KPIListItemBlock>
                  </KPIListItemContentItem>

                  <KPIListItemContentItem itemsCount={3}>
                    <KPIListItemRow>
                      <ListItemWrapper>
                        <Header>
                          <TWTDText>
                            {`Last ${!isMobileView ? 'Full ' : ''}Week`}
                          </TWTDText>

                          {lastWeek && (
                            <DateRange>
                              {`${formatDate(lastWeek?.startDate, 'day', true)} 
                              - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                            </DateRange>
                          )}
                        </Header>
                        {discoveryKPI.pending && (
                          <KPILoader showPrevious showChange />
                        )}
                        {discoveryKPIs && (
                          <KeyValueGroup
                            items={[
                              {
                                label: 'Tags',
                                metric: delve(discoveryKPIs, 'countLastWeek'),
                              },
                              {
                                label: 'Previous',
                                metric: delve(
                                  discoveryKPIs,
                                  'countLastLastWeek'
                                ),
                              },
                              {
                                label: 'Change',
                                metric: delve(
                                  discoveryKPIs,
                                  'wowLastCompleted'
                                ),
                              },
                            ]}
                            compareMode={compareMode}
                            dataColors={dataColors}
                            isKPIValue
                          />
                        )}
                      </ListItemWrapper>
                    </KPIListItemRow>
                    <KPIListItemBlock>
                      <KPIListItemContentItemColumn>
                        <TopTerritories
                          label="Top Countries"
                          pending={discoveryKPI.pending}
                          topTerritories={topCountriesLW}
                          TerritoryItem={TopCountry}
                          fullName={!compareMode}
                          getKey={getCountryKey}
                        />
                      </KPIListItemContentItemColumn>
                      <KPIListItemContentItemColumn>
                        <TopTerritories
                          label={`Top ${countryFiltering} Cities`}
                          pending={discoveryKPI.pending}
                          topTerritories={topCitiesLW}
                          TerritoryItem={TopCities}
                          getKey={getCitiesKey}
                          type="city"
                        />
                      </KPIListItemContentItemColumn>
                    </KPIListItemBlock>
                  </KPIListItemContentItem>

                  <KPIListItemContentItem itemsCount={3}>
                    <KPIListItemRow>
                      <ListItemWrapper>
                        <Header>Release to Date</Header>
                        {discoveryKPI.pending && <KPILoader />}
                        {discoveryKPIs && (
                          <KeyValueGroup
                            items={[
                              {
                                label: 'Tags',
                                metric: delve(discoveryKPIs, 'countRtd'),
                              },
                            ]}
                            compareMode={compareMode}
                            dataColors={dataColors}
                            isKPIValue
                          />
                        )}
                      </ListItemWrapper>
                    </KPIListItemRow>
                  </KPIListItemContentItem>
                </KPIListItemContent>
              </KPIListItem>
            </KPIList>
          </KPIListWrapper>
        </KPIWrapper>
      </ErrorBoundary>
    </Container>
  );
};

export default interceptConnect(dataSources as any)(DiscoveryKPIs as any);
